import { ChevronDown } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useClickOutside } from '@/hooks/useClickOutside';
import { cn } from '@/lib/utils';

interface MultiSelectDropdownProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  renderOption?: (option: string) => string;
  className?: string;
}

export function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  renderOption,
  className,
}: MultiSelectDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => setIsOpen(false), []);
  const ref = useClickOutside<HTMLDivElement>(close, isOpen);

  function handleToggle(option: string) {
    if (selected.includes(option)) {
      onChange(selected.filter((v) => v !== option));
    } else {
      onChange([...selected, option]);
    }
  }

  function handleSelectAll() {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  }

  const isAllSelected = options.length > 0 && selected.length === options.length;
  const hasSelections = selected.length > 0;

  return (
    <div ref={ref} className={cn('relative', className)} onKeyDown={handleKeyDown}>
      <button
        type='button'
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'flex items-center gap-2 px-4 py-2 border rounded-md text-sm bg-white min-w-[150px]',
          'focus:ring-primary focus:border-primary',
          hasSelections ? 'border-primary' : 'border-gray-300',
        )}>
        <span className='truncate'>{label}</span>
        {hasSelections && (
          <span className='inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-primary text-white text-xs font-medium'>
            {selected.length}
          </span>
        )}
        <ChevronDown size={14} className={cn('ml-auto shrink-0 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className='absolute z-50 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg'>
          <button
            type='button'
            onClick={handleSelectAll}
            className='w-full px-3 py-2 text-left text-xs font-medium text-primary hover:bg-gray-50 border-b border-gray-100'>
            {isAllSelected ? t('submissions.filters.deselectAll') : t('submissions.filters.selectAll')}
          </button>
          <div className='max-h-60 overflow-y-auto py-1'>
            {options.map((option) => (
              <label
                key={option}
                className='flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700'>
                <input
                  type='checkbox'
                  checked={selected.includes(option)}
                  onChange={() => handleToggle(option)}
                  className='rounded border-gray-300 text-primary focus:ring-primary'
                />
                <span className='truncate'>{renderOption ? renderOption(option) : option}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
